<template>
  <div
    ref="root"
    class="base-tooltip"
    :class="{ 'base-tooltip--disabled': disabled, 'base-tooltip--short': isShort }"
  >
    <div
      ref="reference"
      class="base-tooltip__trigger"
      aria-describedby="tooltip-content"
      @click.stop="onClick"
    >
      <slot name="trigger">
        <InfoIcon />
      </slot>
    </div>

    <transition name="tooltip-fade">
      <div
        v-if="!disabled && isActiveState"
        ref="floating"
        id="tooltip-content"
        class="base-tooltip__content"
        :style="{ ...floatingStyles, minWidth }"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  type Placement,
} from '@floating-ui/vue';

import InfoIcon from '@kanoAssets/images/icons/info.svg';

import { useTooltipState } from '@kano/composables/ui/tooltip/use-tooltip-state';

interface Props {
  /** External control over the tooltip's visibility. Should override internal state */
  isActive?: boolean
  placement?: Placement
  offset?: number
  trigger?: 'click' | 'hover'
  disabled?: boolean
  isShort?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isActive: undefined,
  placement: 'top',
  offset: 8,
  trigger: 'hover',
});

const root = ref<HTMLElement | null>(null);
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const { floatingStyles } = useFloating(reference, floating, {
  placement: props.placement,
  middleware: [offset(props.offset), flip(), shift({ padding: 4 })],
  whileElementsMounted: autoUpdate,
});

const { isActiveState, onClick } = useTooltipState(root, {
  isActive: toRef(props, 'isActive'),
  trigger: toRef(props, 'trigger'),
});
</script>

<style lang="scss" scoped>
.base-tooltip {
  position: relative;
  display: inline-block;

  &__trigger {
    cursor: help;
  }

  &__content {
    min-width: 220px;
    max-width: 90vw;
    padding: 8px;

    color: var(--color-base-0);
    font-size: 12px;
    line-height: 1.33;
    text-align: left;
    overflow-wrap: break-word;
    white-space: normal;

    background: var(--color-base);
    border-radius: 6px;
    z-index: 990;
  }

  &--disabled {
    .base-tooltip__trigger {
      cursor: default;
      pointer-events: none;
    }
  }

  &--short {
    .base-tooltip__content {
      min-width: auto;
    }
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity .15s ease;
}
.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
